import React, { useCallback, useEffect, useState } from 'react';
import { useAuthenticatedApi } from '../../hooks/useAuthenticatedApi';
import { apiEndpoints } from '../../utils/api';
import { useIntegrationStatus } from '../../contexts/IntegrationStatusContext';

export interface OrgAzureDevOpsStatus {
  connected: boolean;
  ado_username?: string;
  ado_organization_url?: string;
  connected_at?: string;
  token_expires_at?: string;
  pat_invalid?: boolean;
}

interface OrgAzureDevOpsConnectionProps {
  organizationId: string;
  readOnly?: boolean;
}

const OrgAzureDevOpsConnection: React.FC<OrgAzureDevOpsConnectionProps> = ({ organizationId, readOnly = false }) => {
  const api = useAuthenticatedApi();
  const { refresh: refreshIntegrationStatus } = useIntegrationStatus();
  const [status, setStatus] = useState<OrgAzureDevOpsStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [showConnectForm, setShowConnectForm] = useState(false);
  const [form, setForm] = useState({ ado_organization: '', pat: '', pat_expires_at: '' });
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const res = await api.get<OrgAzureDevOpsStatus>(
        apiEndpoints.orgAzureDevOps.status(organizationId, true)
      );
      setStatus(res);
    } catch (e) {
      console.error('Failed to fetch Azure DevOps status', e);
    } finally {
      setLoading(false);
    }
  }, [api, organizationId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const connect = async () => {
    setFormError(null);
    const org = form.ado_organization.trim();
    const pat = form.pat.trim();
    if (!org || !pat) {
      setFormError('Organization name and PAT are required.');
      return;
    }
    if (form.pat_expires_at) {
      const today = new Date().toISOString().split('T')[0];
      if (form.pat_expires_at <= today) {
        setFormError('Token expiration date must be a future date.');
        return;
      }
    }
    setSubmitting(true);
    try {
      await api.post(apiEndpoints.orgAzureDevOps.connect(organizationId), {
        ado_organization_url: `https://dev.azure.com/${org}`,
        pat,
        pat_expires_at: form.pat_expires_at || null,
      });
      setForm({ ado_organization: '', pat: '', pat_expires_at: '' });
      setShowConnectForm(false);
      refreshIntegrationStatus();
      await refresh();
    } catch (e: any) {
      setFormError(e?.data?.detail || e?.message || 'Could not connect to Azure DevOps. Check your organization name and token.');
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDisconnect = async () => {
    setDisconnecting(true);
    try {
      await api.delete(apiEndpoints.orgAzureDevOps.disconnect(organizationId));
      refreshIntegrationStatus();
      await refresh();
      setShowDisconnectModal(false);
    } catch (e) {
      console.error('Failed to disconnect Azure DevOps', e);
    } finally {
      setDisconnecting(false);
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-4 bg-ui-background-tertiary rounded w-3/4 mb-3"></div>
        <div className="h-10 bg-ui-background-tertiary rounded w-32"></div>
      </div>
    );
  }

  if (status?.connected) {
    return (
      <>
        <div className="bg-ui-background-tertiary border border-ui-border-primary rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium text-ui-text-primary">
                {(() => {
                  const url = status.ado_organization_url ?? '';
                  const orgName = url.includes('dev.azure.com/')
                    ? url.split('dev.azure.com/')[1]
                    : url;
                  return status.ado_username ? `${status.ado_username} — ${orgName}` : orgName;
                })()}
              </div>
              {status.connected_at && (
                <div className="text-xs text-ui-text-secondary mt-0.5">
                  Connected {new Date(status.connected_at).toLocaleDateString()}
                </div>
              )}
              {status.pat_invalid && (
                <div className="text-xs mt-0.5 text-red-600 dark:text-red-400">
                  PAT is invalid or revoked — reconnect to restore access
                </div>
              )}
              {status.token_expires_at && (() => {
                const expires = new Date(status.token_expires_at);
                const now = new Date();
                const daysLeft = Math.ceil((expires.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                const isExpired = expires < now;
                const isExpiringSoon = !isExpired && daysLeft <= 7;
                return (
                  <div className={`text-xs mt-0.5 ${isExpired ? 'text-red-600 dark:text-red-400' : isExpiringSoon ? 'text-yellow-600 dark:text-yellow-400' : 'text-ui-text-secondary'}`}>
                    PAT {isExpired ? 'expired' : 'expires'} {expires.toLocaleDateString()}{isExpiringSoon && !isExpired ? ` (${daysLeft} day${daysLeft !== 1 ? 's' : ''} left)` : ''}
                  </div>
                );
              })()}
            </div>
            <button
              onClick={() => setShowDisconnectModal(true)}
              disabled={readOnly}
              className="px-3 py-2 rounded-lg transition-colors text-sm font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/50 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-red-100 dark:disabled:hover:bg-red-900/30"
            >
              Disconnect
            </button>
          </div>
        </div>

        {showDisconnectModal && (
          <div className="fixed inset-0 z-50">
            <div
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => !disconnecting && setShowDisconnectModal(false)}
            />
            <div className="absolute inset-0 flex items-center justify-center p-4">
              <div
                role="dialog"
                aria-modal="true"
                className="w-full max-w-md bg-ui-background-secondary rounded-xl shadow-2xl border border-ui-border-primary overflow-hidden"
              >
                <div className="px-5 py-4 border-b border-ui-border-primary">
                  <h3 className="text-base font-semibold text-ui-text-primary">
                    Disconnect Azure DevOps
                  </h3>
                </div>
                <div className="px-5 py-4 text-sm text-ui-text-secondary">
                  Disconnect Azure DevOps from this organization? All members will lose access to
                  Azure DevOps features until reconnected.
                </div>
                <div className="px-5 py-4 flex justify-end gap-2 border-t border-ui-border-primary">
                  <button
                    className="px-4 py-2 rounded-lg bg-ui-background-tertiary text-ui-text-primary hover:bg-ui-background-tertiary transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                    onClick={() => setShowDisconnectModal(false)}
                    disabled={disconnecting}
                  >
                    Cancel
                  </button>
                  <button
                    className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                    onClick={confirmDisconnect}
                    disabled={disconnecting}
                  >
                    {disconnecting ? 'Disconnecting…' : 'Disconnect'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <div className="space-y-3">
      {!showConnectForm ? (
        <>
          <p className="text-sm text-ui-text-secondary">
            Connect your Azure DevOps organization to start scanning repositories for security issues.
          </p>
          <button
            type="button"
            onClick={() => setShowConnectForm(true)}
            disabled={readOnly}
            className="px-4 py-2 rounded-lg transition-colors shadow-sm bg-brand-primary hover:bg-brand-primaryDark text-black text-sm font-medium disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-brand-primary"
          >
            Connect Azure DevOps
          </button>
        </>
      ) : (
        <div className="grid gap-4 max-w-md">
          <div className="rounded-lg bg-ui-background-tertiary px-4 py-3 text-sm text-ui-text-secondary space-y-1.5">
            <p className="font-medium text-ui-text-secondary">How to create a Personal Access Token</p>
            <ol className="list-decimal list-inside space-y-1">
              <li>In Azure DevOps, click your <strong className="font-medium text-ui-text-secondary">profile avatar</strong> (top right) → <strong className="font-medium text-ui-text-secondary">Personal access tokens</strong></li>
              <li>Click <strong className="font-medium text-ui-text-secondary">New Token</strong> and give it a name (e.g. "kolega")</li>
              <li>Under <strong className="font-medium text-ui-text-secondary">Scopes</strong>, choose <strong className="font-medium text-ui-text-secondary">Custom defined</strong> and grant <strong className="font-medium text-ui-text-secondary">Read</strong> for <strong className="font-medium text-ui-text-secondary">Code</strong> and <strong className="font-medium text-ui-text-secondary">Project and Team</strong></li>
              <li>Click <strong className="font-medium text-ui-text-secondary">Create</strong> and copy the token — it won't be shown again</li>
            </ol>
          </div>
          <div className="grid gap-3">
          {formError && (
            <p className="text-sm text-red-600 dark:text-red-400">{formError}</p>
          )}
          <div className="grid gap-1">
            <label className="text-xs font-medium text-ui-text-secondary">
              Organization ID
            </label>
            <div className="flex items-center rounded border border-ui-border-primary bg-ui-background-secondary overflow-hidden">
              <span className="px-3 py-2 text-sm text-ui-text-tertiary bg-ui-background-tertiary border-r border-ui-border-primary whitespace-nowrap select-none">
                dev.azure.com/
              </span>
              <input
                type="text"
                placeholder="your-org-id"
                value={form.ado_organization}
                onChange={(e) => setForm((f) => ({ ...f, ado_organization: e.target.value }))}
                className="flex-1 px-3 py-2 bg-transparent text-ui-text-primary text-sm outline-none"
              />
            </div>
          </div>
          <div className="grid gap-1">
            <label className="text-xs font-medium text-ui-text-secondary">
              Personal Access Token
            </label>
            <input
              type="password"
              placeholder="Paste your PAT here"
              value={form.pat}
              onChange={(e) => setForm((f) => ({ ...f, pat: e.target.value }))}
              className="w-full px-3 py-2 rounded border border-ui-border-primary bg-ui-background-secondary text-ui-text-primary text-sm"
            />
          </div>
          <div className="grid gap-1">
            <label className="text-xs font-medium text-ui-text-secondary">
              Token expiration date <span className="font-normal text-ui-text-tertiary">(optional)</span>
            </label>
            <input
              type="date"
              title="PAT expiration date (optional)"
              min={new Date(Date.now() + 86400000).toISOString().split('T')[0]}
              value={form.pat_expires_at}
              onChange={(e) => setForm((f) => ({ ...f, pat_expires_at: e.target.value }))}
              className="w-full px-3 py-2 rounded border border-ui-border-primary bg-ui-background-secondary text-ui-text-primary text-sm [&::-webkit-calendar-picker-indicator]:dark:invert"
            />
            <p className="text-xs text-ui-text-tertiary">
              We'll warn you before the token expires so you can rotate it in time.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={connect}
              disabled={submitting || readOnly}
              className="px-4 py-2 rounded-lg bg-brand-primary hover:bg-brand-primaryDark text-black disabled:opacity-60 disabled:cursor-not-allowed text-sm font-medium"
            >
              {submitting ? 'Connecting…' : 'Connect'}
            </button>
            <button
              type="button"
              onClick={() => { setShowConnectForm(false); setFormError(null); }}
              disabled={submitting}
              className="px-4 py-2 rounded-lg transition-colors text-sm bg-ui-background-tertiary text-ui-text-primary hover:bg-ui-background-tertiary"
            >
              Cancel
            </button>
          </div>
        </div>
        </div>
      )}
    </div>
  );
};

export default OrgAzureDevOpsConnection;
